import { atom, getDefaultStore, useAtomValue, useSetAtom, useStore } from 'jotai'
import { ChatMessage, MentionableItem, MetadataEvent, SendMessageOptions, ToolCall } from '../types'
import { useCallback, useMemo } from 'react'
import { GlobalEvents } from '@highlight/ipc'
import type { StorePatch } from '../engine/accumulator'
import type { ChatUsage } from '../engine/events'
import { useSubmitMessage } from '../hooks'
import { useActiveChatId, useGetRealChatId } from './chats'
import {
  applyTranscriptPatches,
  addToolCallInList,
  isTranscriptPatch,
  updateLastMessageInList,
  updateToolCallInList,
  upsertMessageInList,
} from './sink-patches'
import { publishThinkingText } from './thinking-stream'
import { liveChatUsageAtom } from './chat-usage'
import { removeUnadoptedPlaceholder as removeUnadoptedPlaceholderInList } from '../utils/message-reconciliation'
import { getModelIdForMessage } from '../utils/chat'

/**
 * The chat messages are stored in a chatid:messages[] map.
 */
type ChatMessages = Record<string, ChatMessage[]>
const chatMessagesAtom = atom({} as ChatMessages)
// messageId: text : ephemeral
const messageLoaderAtom = atom<Record<string, { text: string; ephemeral: boolean }>>({})
// Track timeouts for ephemeral message loaders
const messageLoaderTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

/**
 * Pending message is used to store the message the user is trying to send.
 * This can be easily re-used for retries.
 */
export type PendingMessage = SendMessageOptions & {
  id: string
  input: string
  options?: SendMessageOptions
}
const pendingMessageAtom = atom<PendingMessage>()

/**
 * The loading message chat indicates which message is currently streaming
 * a response from the backend.
 */
const streamingMessageMetadata = atom<MetadataEvent>()

/**
 * Get all chat messages with a read-only value.
 *
 * This subscribes to every chat's transcript, so a component using it re-renders
 * on every streaming token. Callers that only need the value at call time (not a
 * re-render when it changes) should use `useReadAllChatMessages` instead.
 */
export const useAllChatMessages = () => {
  return useAtomValue(chatMessagesAtom)
}

/**
 * Read all chat messages without subscribing.
 *
 * For the async and event-driven callers — the SSE host seeding its accumulators,
 * the loader merging a fetch — that want the current transcript when they run,
 * not a render per delta.
 */
export const useReadAllChatMessages = () => {
  const store = useStore()
  return useCallback(() => store.get(chatMessagesAtom), [store])
}

/**
 * Handle all message CRUD related actions.
 */
export const useMessagesStore = () => {
  const setChatMessages = useSetAtom(chatMessagesAtom)

  const updateMessagesChatId = useCallback(
    (oldChatId: string, newChatId: string) => {
      setChatMessages((prev) => {
        const oldMessages = prev[oldChatId] ?? []
        const updatedMessages = oldMessages.map((message) => ({
          ...message,
          conversation_id: newChatId,
        }))

        const newChats = { ...prev }
        delete newChats[oldChatId]

        return {
          ...newChats,
          [newChatId]: updatedMessages,
        }
      })
    },
    [setChatMessages],
  )

  const setMessages = useCallback(
    (chatId: string, messages?: ChatMessage[]) => {
      setChatMessages((prev) => ({
        ...prev,
        [chatId]: messages ?? ([] as ChatMessage[]),
      }))
    },
    [setChatMessages],
  )

  const clearMessages = useCallback(
    (chatId: string) => {
      setChatMessages((prev) => {
        const newChats = { ...prev }
        delete newChats[chatId]
        return newChats
      })
    },
    [setChatMessages],
  )

  /** Appends `message` verbatim; placeholder adoption lives in the sink-patch path (see sink-patches.ts). */
  const appendMessage = useCallback(
    (chatId: string, message: ChatMessage) => {
      setChatMessages((prev) => ({
        ...prev,
        [chatId]: [...(prev[chatId] ?? []), message],
      }))
    },
    [setChatMessages],
  )

  const upsertMessage = useCallback(
    (chatId: string, message: ChatMessage) => {
      setChatMessages((prev) => ({
        ...prev,
        [chatId]: upsertMessageInList(prev[chatId] ?? [], message),
      }))
    },
    [setChatMessages],
  )

  const removeUnadoptedPlaceholder = useCallback(
    (chatId: string, clientId: string) => {
      setChatMessages((prev) => {
        const chatMessages = prev[chatId]
        if (!chatMessages) {
          return prev
        }
        const next = removeUnadoptedPlaceholderInList(chatMessages, clientId)
        if (next === chatMessages) {
          return prev
        }
        return {
          ...prev,
          [chatId]: next,
        }
      })
    },
    [setChatMessages],
  )

  const updateLastMessage = useCallback(
    (chatId: string, message: Partial<ChatMessage>) => {
      setChatMessages((prev) => {
        const chatMessages = prev[chatId]
        if (!chatMessages) {
          return {
            ...prev,
            [chatId]: [message as ChatMessage],
          }
        }
        const updated = updateLastMessageInList(chatMessages, message)
        return updated === chatMessages ? prev : { ...prev, [chatId]: updated }
      })
    },
    [setChatMessages],
  )

  const removeLastMessage = useCallback(
    (chatId: string, message: Partial<ChatMessage>) => {
      setChatMessages((prev) => {
        const chatMessages = prev[chatId]
        if (!chatMessages) {
          return prev
        }
        const lastIndex = chatMessages.findLastIndex((msg) => msg.id === message.id || msg.role === message.role)
        if (lastIndex === -1) {
          return prev
        }
        return {
          ...prev,
          [chatId]: chatMessages.toSpliced(lastIndex, 1),
        }
      })
    },
    [setChatMessages],
  )

  const addToolCall = useCallback(
    (chatId: string, messageId: string, toolCall: ToolCall) => {
      setChatMessages((prev) => {
        const chatMessages = prev[chatId] ?? []
        const updated = addToolCallInList(chatMessages, messageId, toolCall)
        return updated === chatMessages ? prev : { ...prev, [chatId]: updated }
      })
    },
    [setChatMessages],
  )

  const updateToolCall = useCallback(
    (chatId: string, toolCallId: string, updates: Partial<ToolCall>) => {
      let emitSkillsChanged = false

      setChatMessages((prev) => {
        const chatMessages = prev[chatId] ?? []
        const { messages: updated, skillsChanged } = updateToolCallInList(chatMessages, toolCallId, updates)
        emitSkillsChanged = skillsChanged
        return updated === chatMessages ? prev : { ...prev, [chatId]: updated }
      })

      if (emitSkillsChanged) {
        GlobalEvents.emit('skills:changed')
      }
    },
    [setChatMessages],
  )

  const patchMessage = useCallback(
    (chatId: string, messageId: string, patch: Partial<ChatMessage>) => {
      setChatMessages((prev) => {
        const messages = prev[chatId] ?? []
        const idx = messages.findIndex((m) => m.id === messageId)
        if (idx === -1) return prev
        const updated = [...messages]
        updated[idx] = { ...updated[idx], ...patch }
        return { ...prev, [chatId]: updated }
      })
    },
    [setChatMessages],
  )

  return useMemo(() => {
    return {
      setMessages,
      clearMessages,
      appendMessage,
      upsertMessage,
      removeUnadoptedPlaceholder,
      updateLastMessage,
      removeLastMessage,
      updateMessagesChatId,
      addToolCall,
      updateToolCall,
      patchMessage,
    }
  }, [
    setMessages,
    clearMessages,
    appendMessage,
    upsertMessage,
    removeUnadoptedPlaceholder,
    updateLastMessage,
    removeLastMessage,
    updateMessagesChatId,
    addToolCall,
    updateToolCall,
    patchMessage,
  ])
}

/**
 * Derive a chat message atom value for a specific chat ID.
 * @param chatId
 */
export const useChatMessages = (chatId?: string) => {
  const activeChatId = useActiveChatId()

  const id = useMemo(() => {
    return chatId ?? activeChatId
  }, [chatId, activeChatId])

  // Use useMemo to ensure a stable atom reference
  const chatAtom = useMemo(
    () =>
      atom((get) => {
        if (!id) {
          return []
        }
        const chatMessages = get(chatMessagesAtom)
        return chatMessages[id] || []
      }),
    [id], // Recreate the atom only when chatId changes
  )

  // Only read the message values in this case
  return useAtomValue(chatAtom)
}

/**
 * Derive just the message count for a chat.
 *
 * Chrome that only gates on "has messages" / "how many" would otherwise
 * subscribe to the whole transcript and re-render on every streaming token.
 */
export const useChatMessageCount = (chatId?: string) => {
  const activeChatId = useActiveChatId()
  const id = chatId ?? activeChatId

  const countAtom = useMemo(
    () =>
      atom((get) => {
        if (!id) return 0
        return (get(chatMessagesAtom)[id] ?? []).length
      }),
    [id],
  )

  return useAtomValue(countAtom)
}

/**
 * The model the chat's next message will use, resolved the way the submit path
 * resolves it. Derives just the id, so streaming tokens don't re-render the caller.
 */
export const useNextMessageModelId = (
  chatId: string | undefined,
  {
    mentions,
    defaultModel,
    modelChoice,
  }: { mentions: Array<MentionableItem>; defaultModel: string | undefined; modelChoice: boolean },
): string | undefined => {
  const modelIdAtom = useMemo(
    () =>
      atom((get) =>
        getModelIdForMessage({
          mentions,
          isExistingChat: !!chatId,
          conversationId: chatId ?? '',
          // Without a chat there is no history to read, so skip the per-token subscription.
          allMessages: chatId ? get(chatMessagesAtom) : {},
          defaultModel,
          modelChoice,
        }),
      ),
    [chatId, mentions, defaultModel, modelChoice],
  )
  return useAtomValue(modelIdAtom)
}

export const useLastUserMessage = (role: 'user' | 'assistant') => {
  const messages = useChatMessages()
  if (!messages.length) {
    return null
  }
  const lastIndex = messages.findLastIndex((m) => m.role === role)
  if (lastIndex === -1) {
    return null
  }
  return messages[lastIndex]
}

/**
 * Pending message get and set hooks.
 */
export const usePendingMessage = () => {
  return useAtomValue(pendingMessageAtom)
}
export const useSetPendingMessage = () => {
  return useSetAtom(pendingMessageAtom)
}

export const useSetStreamingMetadata = () => {
  return useSetAtom(streamingMessageMetadata)
}

/**
 * Streaming metadata gated on conversation match. The metadata atom is global —
 * every stream writes it — so an ungated read can surface another chat's
 * active-turn metadata (favicons, model, search state). Returns the metadata
 * only when it belongs to `chatId`. Both sides are mapped through the
 * temp→real id mapping: writers stamp whichever id they hold at event time
 * (the submit path's local id is only reassigned on the metadata event), so a
 * raw compare would drop own-chat metadata across the swap.
 *
 * Interim R9 gate: to be subsumed by the engine's per-chat
 * `useTurnStatus(chatId)` in phase 4.
 *
 * Passing `undefined` reads nothing: the derived atom never touches the
 * metadata atom, so history rows — which all mount the same container type and
 * call this hook unconditionally — hold no metadata subscription.
 */
export const useStreamingMetadataForChat = (chatId: string | undefined): MetadataEvent | undefined => {
  const getRealChatId = useGetRealChatId()
  const derived = useMemo(
    () =>
      atom((get) => {
        if (!chatId) return undefined
        const streamingMetadata = get(streamingMessageMetadata)
        if (!streamingMetadata) return undefined
        return getRealChatId(streamingMetadata.conversation_id) === getRealChatId(chatId)
          ? streamingMetadata
          : undefined
      }),
    [chatId, getRealChatId],
  )
  return useAtomValue(derived)
}

/**
 * Handle retrying the pending message.
 */
export const useRetryPendingMessage = () => {
  const activeChatId = useActiveChatId()
  const pendingMessage = usePendingMessage()
  const { removeLastMessage } = useMessagesStore()
  const submitMessage = useSubmitMessage()
  return useCallback(async () => {
    if (!pendingMessage || !activeChatId) {
      return false
    }
    removeLastMessage(activeChatId, {
      id: pendingMessage.id,
      role: 'user',
    })
    submitMessage(pendingMessage.input, pendingMessage.options)
  }, [activeChatId, pendingMessage, submitMessage, removeLastMessage])
}

export const useMessageLoader = (messageId: string) => {
  const messageLoader = useAtomValue(messageLoaderAtom)
  return useMemo(() => {
    return messageLoader[messageId]
  }, [messageLoader, messageId])
}

/**
 * The header message a loader should attach to. If the message is part of a
 * sequential block (an assistant message directly following another, or a
 * tool message after its assistant), the loader belongs on the *nearest
 * previous* assistant message so the block's header — which represents that
 * assistant message — shows it. Blank user messages are skipped in the walk.
 */
function resolveLoaderTarget(messages: ReadonlyArray<ChatMessage>, messageId: string): string {
  const currentIndex = messages.findIndex((m) => m.id === messageId)
  if (currentIndex === -1) return messageId
  if (messages[currentIndex].role === 'assistant') return messageId

  for (let i = currentIndex - 1; i >= 0; i--) {
    const prev = messages[i]
    if (prev.role === 'assistant') {
      return prev.id
    }
    const isBlankUser =
      prev.role === 'user' && (prev.content === '' || prev.content === undefined || prev.content === null)
    if (!isBlankUser) {
      break
    }
  }
  return messageId
}

/** The loader's header target, resolved against the owning chat's transcript. */
function loaderTargetFor(chatId: string, messageId: string): string {
  const messages = getDefaultStore().get(chatMessagesAtom)[chatId]
  return messages ? resolveLoaderTarget(messages, messageId) : messageId
}

function clearLoaderTimeout(targetMessageId: string): void {
  const existingTimeout = messageLoaderTimeouts.get(targetMessageId)
  if (existingTimeout) {
    clearTimeout(existingTimeout)
    messageLoaderTimeouts.delete(targetMessageId)
  }
}

/**
 * Store-bound loader writers — driven by the engine sink and other non-React
 * lifecycle code, same pattern as turn-status.ts.
 */
export function addMessageLoader(chatId: string, messageId: string, text: string, ephemeral: boolean = false): void {
  const targetMessageId = loaderTargetFor(chatId, messageId)
  clearLoaderTimeout(targetMessageId)

  getDefaultStore().set(messageLoaderAtom, (prev) => ({
    ...prev,
    [targetMessageId]: { text, ephemeral },
  }))

  // If ephemeral, set timeout to remove after 5 seconds
  if (ephemeral) {
    const timeoutId = setTimeout(() => {
      getDefaultStore().set(messageLoaderAtom, (prev) => {
        const rest = { ...prev }
        delete rest[targetMessageId]
        return rest
      })
      messageLoaderTimeouts.delete(targetMessageId)
    }, 5000)

    messageLoaderTimeouts.set(targetMessageId, timeoutId)
  }
}

/** Remove the loader from the same header message it was attached to. */
export function removeMessageLoader(chatId: string, messageId: string): void {
  const targetMessageId = loaderTargetFor(chatId, messageId)
  clearLoaderTimeout(targetMessageId)

  getDefaultStore().set(messageLoaderAtom, (prev) => {
    const rest = { ...prev }
    delete rest[targetMessageId]
    return rest
  })
}

/**
 * Apply one frame's worth of engine `StorePatch`es (HIG-5964/R4b) — the
 * coalescing sink's flush target. All transcript patches fold into a single
 * `chatMessagesAtom` write via `applyTranscriptPatches`; the side channels
 * coalesce per flush too (last metadata wins, last thinking text per message
 * and last usage per chat win — all cumulative snapshots, not deltas). Loaders apply after the
 * transcript so their header-target walk sees the fresh record — per patch,
 * not folded: they ride tool lifecycle events (a handful per turn, never the
 * per-delta hot path), carry timeout side effects, and React batches the
 * flush's renders into one pass regardless.
 */
export function applySinkPatches(patches: ReadonlyArray<StorePatch>): void {
  const store = getDefaultStore()

  const transcript = patches.filter(isTranscriptPatch)
  if (transcript.length > 0) {
    let emitSkillsChanged = false
    store.set(chatMessagesAtom, (prev) => {
      const { record, skillsChanged } = applyTranscriptPatches(prev, transcript)
      emitSkillsChanged = skillsChanged
      return record
    })
    if (emitSkillsChanged) {
      GlobalEvents.emit('skills:changed')
    }
  }

  const thinkingByMessage = new Map<string, string>()
  const usageByChat = new Map<string, ChatUsage>()
  let lastMetadata: MetadataEvent | undefined
  for (const patch of patches) {
    if (patch.op === 'publishThinking') {
      thinkingByMessage.set(patch.messageId, patch.text)
    } else if (patch.op === 'setStreamingMetadata') {
      lastMetadata = patch.metadata
    } else if (patch.op === 'setChatUsage') {
      usageByChat.set(patch.chatId, patch.usage)
    }
  }
  if (usageByChat.size > 0) {
    store.set(liveChatUsageAtom, (prev) => ({ ...prev, ...Object.fromEntries(usageByChat) }))
  }
  for (const [messageId, text] of thinkingByMessage) {
    publishThinkingText(messageId, text)
  }
  if (lastMetadata !== undefined) {
    store.set(streamingMessageMetadata, lastMetadata)
  }

  for (const patch of patches) {
    if (patch.op === 'addMessageLoader') {
      addMessageLoader(patch.chatId, patch.messageId, patch.text, patch.ephemeral)
    } else if (patch.op === 'removeMessageLoader') {
      removeMessageLoader(patch.chatId, patch.messageId)
    }
  }
}
